import React, { useState, useCallback } from 'react'
import { connect } from 'react-redux'

import { downloadStringAsFile } from './../../../../Utils/Misc'
import { escapeCsvString } from './../../../../shared/csv'
import { numberFormatter } from './../../../../Utils/Formatters'
import { getMasterScheduleData, getDataForMissionsImport } from './gql'
import {
  header,
  sortOn,
  getDynamicHeaders,
  exportHeaders,
} from './tableSettings'
import {
  getMasterScheduleMissionData,
  getMasterScheduleMissionExportData,
  formatData,
  formatDataExport,
  getFilters,
} from './util'
import { normalizeGraphQLConnection } from '../../../../shared/graphql/utils'
import {
  SORT_OPTIONS,
  getStartEndDateFromWeekFilter,
} from '../../../../Utils/Table'
import { MISSION_STATUS_ENUM } from '../../../../shared/constants'
import { createMissions, getCampaignByChunks } from '../../../../Module/graphQL'
import { getMasterScheduleAndGlobalPlanningFiltersPresets } from '../../../../Utils/GlobalSearch/GlobalSearchUtils'
import { getCurrentTimeOfDay } from '../../../../Utils/Date'
import { gqlCampaign, gqlCityPlans, gqlMissions } from './gql'
import { authorization } from '../../../../Module/Auth/Auth'

const Controller = ({
  children,
  campaign,
  getCampaignResults,
  ...otherProps
}) => {
  const itemsPerPage = 50
  const [data, setData] = useState({
    missions: [],
    cityPlans: [],
    brandAmbassadors: [],
  })
  const [refreshRequestTrigger, setRefreshRequestTrigger] = useState()
  const [isExportLoading, setIsExportLoading] = useState(false)

  const { name: campaignName } = campaign

  const getData = async ({ filter, first, orderBy, skip = 0, ...filters }) => {
    const { startDate, endDate } = getStartEndDateFromWeekFilter(filters.Week)
    const { data } = await getMasterScheduleData({
      campaignId: campaign.id,
      first: itemsPerPage,
      after: Buffer.from(String(skip)).toString('base64'),
      orderBy: SORT_OPTIONS[filters.orderBy || orderBy],
      locationId: filters['Location'],
      timeOfDay: filters['Time of day'],
      statusAndAbove: filters['Status']
        ? filters['Status']
        : MISSION_STATUS_ENUM.UNDER_REVIEW,
      statusAndBelow: filters['Status'],
      dayOfTheWeek: filters['Day of week'],
      cityId: filters['City'],
      filter,
      startDate,
      endDate,
    })

    const formattedData = formatData(
      data,
      campaign,
      getMasterScheduleMissionData,
    )

    setData(data => ({ ...data, ...formattedData }))

    return formattedData
  }

  // Export all the data as a file
  const onExportData = async () => {
    setIsExportLoading(true)
    const missionStatuses = authorization.isClient()
      ? [
          MISSION_STATUS_ENUM.UNDER_REVIEW,
          MISSION_STATUS_ENUM.COMPLETED,
          MISSION_STATUS_ENUM.PAID,
        ]
      : [
          MISSION_STATUS_ENUM.UNDER_REVIEW,
          MISSION_STATUS_ENUM.COMPLETED,
          MISSION_STATUS_ENUM.PAID,
          MISSION_STATUS_ENUM.CANCELLED,
        ]
    const result = await getCampaignByChunks({
      campaignId: campaign.id,
      cityPlanChunk: 1,
      missionChunk: 200,
      missionStatuses,
      queries: { gqlCampaign, gqlCityPlans, gqlMissions },
    })
    const { missions } = formatDataExport(
      result.cityPlans.nodes,
      campaign,
      getMasterScheduleMissionExportData,
    )
    const exportMode = true
    const exportHeadersGenerated = getDynamicHeaders(
      exportHeaders,
      campaign.metrics.nodes,
      exportMode,
    )

    var content = exportHeadersGenerated.join(',')
    missions.forEach(m => {
      content +=
        '\n' +
        exportHeadersGenerated
          .map(k => {
            var val = m[k]
            return typeof val === 'string' ? escapeCsvString(val) : val
          })
          .join(',')
    })
    downloadStringAsFile(content, campaignName + '.csv', 'text/csv')
    setIsExportLoading(false)
  }

  const filters = getFilters({
    cities: data.cityPlans.map(({ city }) => city),
    locations: data.cityPlans.reduce(
      (acc, { city }) => [
        ...acc,
        ...normalizeGraphQLConnection(city.locations),
      ],
      [],
    ),
  })
  // Bit crap but don't want to break filters if they are shared somewhere else.
  filters[filters.findIndex(f => f.label == 'Status')] = {
    label: 'Status',
    options: [
      { label: 'Under Review', value: 'UNDER_REVIEW' },
      { label: 'Completed', value: 'COMPLETED' },
      { label: 'Paid', value: 'PAID' },
      { label: 'Cancelled', value: 'CANCELLED' },
    ],
  }

  const getDataForMissionsImportHandler = async () => {
    const { data } = await getDataForMissionsImport(campaign.id)
    const brandAmbassadors = data.campaign.cityPlans.nodes.reduce(
      (acc, cityPlan) => {
        const {
          city: { brandAmbassadors },
        } = cityPlan
        acc = [...acc, ...normalizeGraphQLConnection(brandAmbassadors)]
        return acc
      },
      [],
    )

    setData(data => ({ ...data, brandAmbassadors }))
  }

  const onMissionsImportHandler = useCallback(async missions => {
    await createMissions(missions)

    setRefreshRequestTrigger(new Date())
  })

  const city = data.missions[0] ? data.missions[0].cityPlan.city : {}
  const { afternoonCheckInTime, lunchCheckInTime, timezone } = city
  const currentTimeOfDay = getCurrentTimeOfDay({
    afternoonCheckInTime,
    lunchCheckInTime,
    timezone,
  })
  const presets = getMasterScheduleAndGlobalPlanningFiltersPresets({
    currentTimeOfDay,
  })

  return React.cloneElement(children, {
    ...otherProps,
    campaign,
    cityPlans: data.cityPlans,
    missions: data.missions,
    brandAmbassadors: data.brandAmbassadors,
    header: getDynamicHeaders(header, campaign.metrics.nodes),
    sortOn,
    filterOn: filters,
    footerRender: getMasterScheduleFooterData,
    onMissionsImport: onMissionsImportHandler,
    refreshRequestTrigger,
    onExportData,
    action: data => window.open(`/dashboard/mission/${data.id}`, '_blank'),
    fetchData: getData,
    getDataForMissionsImport: getDataForMissionsImportHandler,
    presets,
    isExportLoading,
  })
}

const getMasterScheduleFooterData = missions => {
  const data = {
    'City': <strong>Total:</strong>,
    'Flyers distributed': (
      <strong>
        {numberFormatter(
          missions.reduce((acc, mission) => {
            var distributed = Number(mission['Flyers distributed'])
            return acc + (isNaN(distributed) ? 0 : distributed)
          }, 0),
        )}
      </strong>
    ),
  }
  const footerData = [data]
  return footerData
}

const mapStateToProps = (state, props) => ({
  campaign: state.campaigns.campaigns[props.campaignId],
})

export default connect(mapStateToProps)(Controller)



// WEBPACK FOOTER //
// ./src/Containers/Campaigns/Page/MasterSchedule/Controller.js